import React, { useEffect, useMemo, useState } from "react";
import { withTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import PropTypes from "prop-types";
import { omit } from "lodash";
import * as Yup from "yup";
import { useFormik } from "formik";

import NameTransferApplicantDetails from "pages/Consumer/ApplicationType/NameTransferConnection/NameTransferApplicantDetails";
import Stepper from "common/Stepper";
import BreadCrumb from "Components/Common/BreadCrumb";
import AuthorizedPersonDetails from "Components/AuthorizedPersonDetails/AuthorizedPersonDetails";
import DocumentToSubmit from "Components/DocumentsToSubmit";
import Declaration from "Components/Declaration";
import { applicationTypes, minHtContractDemandLoad } from "common/constant";
import Backdrop from "Components/Common/Backdrop";

import { setValues } from "slices/nameTransferConnection/reducer";
import { useNameTransferValidationSchema } from "hooks";

import { getIsDocumentRequired } from "utils/getIsDocumentRequired";
import {
  generateFormData,
  filterEmptyStrings,
  convertDocumentKeys,
} from "utils";

import {
  useLazyApplicationDetailQuery,
  usePostApplicationDetailsMutation,
} from "apis/ApplicationRequestDetail";
import { useLazyConsumerByIdQuery } from "apis/User";
import { myRequestRoute } from "Routes/constants";

const NameTransfer = ({ applicationId, handleClose = () => {}, t }) => {
  const [applicationData, setApplicationData] = useState({});
  const [currentStep, setCurrentStep] = useState(0);
  const user = useSelector((state) => state.Login.user);
  const navigate = useNavigate();

  const [submitApplication, { isLoading }] =
    usePostApplicationDetailsMutation();

  const [
    getUtilityConsumerDetails,
    {
      data: { data: consumerData } = {},
      isLoading: isConsumerDetailsLoading,
      isFetching: isConsumerDataFetching,
    },
  ] = useLazyConsumerByIdQuery();
  const [
    getApplicationDetails,
    {
      isLoading: isApplicationDataLoading,
      isFetching: isApplicationDataFetching,
    },
  ] = useLazyApplicationDetailQuery();

  const getValidationSchema = useNameTransferValidationSchema();

  const dispatch = useDispatch();
  const initialValues = useMemo(
    () => ({
      id: applicationId ?? 0,
      utilityId: 1,
      applicationTypeId: applicationTypes["Name Change/Transfer"],
      utilityConsumerId:
        applicationData?.utilityConsumerId ?? consumerData?.id ?? "",
      consumerName: consumerData?.consumerName || "",
      consumerNumber:
        applicationData?.consumerNumber ?? consumerData?.consumerNumber ?? "",
      applicant: {
        id: applicationData?.applicant?.id ?? 0,
        applicantTypeId: applicationData?.applicant?.applicantType?.id ?? "",
        applicantName: applicationData?.applicant?.applicantName ?? "",
        panNumber: applicationData?.applicant?.panNumber ?? "",
        gstNumber: applicationData?.applicant?.gstNumber ?? "",
        occupancyTypeId: applicationData?.applicant?.occupancyType?.id ?? "",
        isGSTRegistered: applicationData?.applicant?.isGSTRegistered ?? "",
        addressType: applicationData?.applicant?.addressType ?? "",
        correspondenceAddress:
          applicationData?.applicant?.correspondenceAddress ?? "",
        city: applicationData?.applicant?.city ?? "",
        pinCode: applicationData?.applicant?.pinCode ?? "",
        email: applicationData?.applicant?.email ?? "",
        mobileNumber: applicationData?.applicant?.mobileNumber ?? "",
        landLine: applicationData?.applicant?.landLine ?? "",
        alternateEmail: applicationData?.applicant?.alternateEmail
          ? applicationData?.applicant?.alternateEmail?.split(", ")
          : [],
        alternateMobileNumber:
          applicationData?.applicant?.alternateMobileNumber ?? "",
        eBillConfirmation: true,
      },
      authorizedPerson: {
        isAuthorizedPerson: applicationData?.authorizedPerson
          ? applicationData.authorizedPerson.email ===
              applicationData.user.email &&
            applicationData.authorizedPerson.mobileNumber ===
              applicationData.user.mobileNumber
          : true,
        isEditablePerson:
          applicationData?.authorizedPerson?.isEditablePerson ?? false,
        id: applicationData?.authorizedPerson?.id ?? 0,
        salutation: applicationData?.authorizedPerson?.salutation ?? "",
        firstName: applicationData?.authorizedPerson?.firstName ?? "",
        middleName: applicationData?.authorizedPerson?.middleName ?? "",
        lastName: applicationData?.authorizedPerson?.lastName ?? "",
        designation: applicationData?.authorizedPerson?.designation ?? "",
        email: applicationData?.authorizedPerson?.email ?? "",
        mobileNumber: applicationData?.authorizedPerson?.mobileNumber ?? "",
        landLine: applicationData?.authorizedPerson?.landLine ?? "",
        image: "",
        imageUrl: applicationData?.authorizedPerson?.imageUrl ?? "",
        isUnRegisteredPerson: applicationData?.authorizedPerson
          ? applicationData.authorizedPerson.email !== user.email &&
            applicationData.authorizedPerson.mobileNumber !== user.mobileNumber
          : false,
      },
      utilityAddress: {
        premisesAreaId: consumerData?.utilityAddress?.premiseArea?.id ?? "",
      },
      connectionLoad: {
        powerRequirement:
          applicationData?.connectionLoad?.powerRequirement ??
          consumerData?.connectionLoad?.powerRequirement ??
          "",
        powerRequirementUnit:
          (applicationData?.connectionLoad?.totalPowerRequirement ??
            consumerData?.connectionLoad?.totalPowerRequirement) <= 99
            ? 1
            : 2 ?? "",
        tention:
          applicationData?.connectionLoad?.totalPowerRequirement ||
          consumerData?.connectionLoad?.totalPowerRequirement
            ? (applicationData?.connectionLoad?.totalPowerRequirement ||
                consumerData?.connectionLoad?.totalPowerRequirement) <
              minHtContractDemandLoad
              ? "LT"
              : "HT"
            : "",
        loadDetails:
          applicationData?.connectionLoad?.loadDetail ??
          consumerData?.connectionLoad?.loadDetail ??
          "",
        supplyVoltage: applicationData?.connectionLoad?.supplyVoltage
          ? +applicationData?.connectionLoad?.supplyVoltage
          : consumerData?.connectionLoad?.supplyVoltage ?? "",
        supplyVoltageUnit:
          applicationData?.connectionLoad?.supplyVoltageUnit ??
          consumerData?.connectionLoad?.supplyVoltageUnit ??
          "",
        tariffCategory:
          applicationData?.connectionLoad?.tariffCategory ??
          consumerData?.tariffName ??
          "",
        isGreenPowerRequired: !!consumerData?.connectionLoad?.greenPower,
        usagePurpose: consumerData?.utilityAddress?.premiseUsagePurpose ?? "",
      },
      declaration: {
        declarationId: applicationData?.declaration?.id ?? 0,
        isConsumerDuesOutstanding:
          !!applicationData?.declaration?.consumerDuesOutstanding ?? false,
        consumerDuesOutstanding:
          applicationData?.declaration?.consumerDuesOutstanding ?? "",
        isPremisesDuesOutstanding:
          !!applicationData?.declaration?.premisesDuesOutstanding ?? false,
        premisesDuesOutstanding:
          applicationData?.declaration?.premisesDuesOutstanding ?? "",
        isFirmDuesOutstanding:
          !!applicationData?.declaration?.firmDuesOutstanding ?? false,
        firmDuesOutstanding:
          applicationData?.declaration?.firmDuesOutstanding ?? "",
      },
      extraValues: {
        requiredDocumentsCount: 0,
        proofOfOwnershipTypes: [],
        userMobileNumber: applicationData?.user?.mobileNumber ?? "",
        userEmail: applicationData?.user?.email ?? "",
      },
      inProgressApplicationId: consumerData?.inProgressApplicationId ?? 0,
      isAllApplicationsProcessCompleted:
        consumerData?.isAllApplicationsProcessCompleted ?? false,
      payableBillAmount: consumerData?.payableBillAmount ?? 0,
      parentId:
        applicationData?.documents?.find((document) => document.parentId)
          ?.parentId ?? "",
      documents: applicationData?.documents?.filter(({ id }) => id) ?? [],
      isDraft: true,
      consumerData: consumerData || null,
      proofOfOwnershipDescription: "",
      isIGSTExempted: consumerData?.isIGSTExempted ?? false,
    }),
    [applicationData, consumerData]
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: !(
      (consumerData?.inProgressApplicationId !== applicationData?.id &&
        !consumerData?.isAllApplicationsProcessCompleted) ||
      consumerData?.payableBillAmount > 0
    )
      ? getValidationSchema(currentStep)
      : Yup.object({}),

    onSubmit: async (values, actions) => {
      try {
        if (currentStep === steps.length - 1) {
          createNameTransferConnection(values);
        }
        // else if (values.payableBillAmount > 0) {
        // validating if the details changed or not
        // const consumerDataWithoutNull = JSON.parse(
        //   JSON.stringify(consumerData, (_, value) =>
        //     value === null ? "" : value
        //   )
        // );

        // const existingData = omit(consumerDataWithoutNull.applicant, [
        //   "id",
        //   "applicantType",
        //   "occupancyType",
        // ]);
        // const updatedData = omit(values.applicant, "id");

        // const {
        //   applicant: { applicantType, occupancyType },
        // } = consumerDataWithoutNull;

        // const isDataChanged = !isEqual(
        //   { ...updatedData, mobileNumber: +updatedData.mobileNumber },
        //   {
        //     ...existingData,
        //     applicantTypeId: applicantType.id,
        //     occupancyTypeId: occupancyType.id,
        //     mobileNumber: +existingData.mobileNumber,
        //   }
        // );

        // if (!isDataChanged) {
        //   toast.warning("Please change the applicant details");
        // } else
        else if (
          values?.inProgressApplicationId !== applicationData?.id &&
          !values.isAllApplicationsProcessCompleted
        ) {
          toast.error(
            "There are some incomplete applications associated with this consumer number. You are unable to fill the form."
          );
        } else if (values.payableBillAmount > 0) {
          toast.error(
            `You have an unpaid amount of ${values.payableBillAmount} RS. Please pay first to continue.`
          );
        } else {
          setCurrentStep((prev) => prev + 1);
          actions.setTouched({});
        }
      } catch (error) {
        console.error(error);
      }
    },
  });
  const { values, initialValues: formikInitialValues } = formik;

  const {
    documents,
    extraValues: { requiredDocumentsCount },
  } = values;

  const steps = useMemo(
    () => [
      {
        id: 1,
        name: "Applicant Details",
        isCompleted: currentStep >= 1,
        component: <NameTransferApplicantDetails formik={formik} />,
      },
      {
        id: 2,
        name: "Authorized Person Details",
        isCompleted: currentStep >= 2,
        component: <AuthorizedPersonDetails formik={formik} />,
      },
      {
        id: 3,
        name: "Document to Submit",
        isCompleted: currentStep >= 3,
        component: <DocumentToSubmit formik={formik} />,
      },
      {
        id: 4,
        name: "Declaration",
        isCompleted: currentStep === 4,
        component: <Declaration formik={formik} />,
      },
    ],
    [currentStep, formik]
  );

  useEffect(() => {
    dispatch(setValues(values));
  }, [values, dispatch]);

  useEffect(() => {
    if (applicationId) {
      getApplicationData(applicationId);
      // setCompletedSteps(steps.length);
    }
  }, [applicationId]);

  const getCustomerDetails = async (utilityConsumerId) => {
    try {
      await getUtilityConsumerDetails(utilityConsumerId);
      if (
        applicationId &&
        values.utilityConsumerId !== formikInitialValues.utilityConsumerId
      ) {
        setApplicationData({});
      }
    } catch (error) {}
  };

  useEffect(() => {
    if (values.utilityConsumerId) {
      getCustomerDetails(values.utilityConsumerId);
    }
  }, [values.utilityConsumerId]);

  const validateDocument = () => {
    const requiredUploadedDocumentsCount = documents.filter(
      ({ documentRequiredEnum }) => getIsDocumentRequired(documentRequiredEnum)
    ).length;

    if (
      currentStep === 2 &&
      requiredUploadedDocumentsCount !== requiredDocumentsCount
    ) {
      toast.error("Please upload all required documents");
    }
  };
  const getApplicationData = async (applicationId) => {
    try {
      const res = await getApplicationDetails(applicationId);
      setApplicationData(res?.data);
    } catch (error) {}
  };

  const createNameTransferConnection = async (values, isDraft = false) => {
    const imageKeyToRemove = values.authorizedPerson.image
      ? "authorizedPerson.imageUrl"
      : "authorizedPerson.image";
    const cityId = values.applicant.city?.id ?? "";

    // converts document array to key-value pair
    const payloadWithUpdatedDocumentKeys = convertDocumentKeys(values);

    const applicationDetails = omit(
      {
        ...payloadWithUpdatedDocumentKeys,
        applicant: {
          ...payloadWithUpdatedDocumentKeys.applicant,
          alternateEmail: payloadWithUpdatedDocumentKeys.applicant
            .alternateEmail.length
            ? payloadWithUpdatedDocumentKeys.applicant.alternateEmail.join(", ")
            : "",
          cityId,
        },
        isDraft,
      },
      [
        "applicant.city",
        "inProgressApplicationId",
        "isAllApplicationsProcessCompleted",
        "payableBillAmount",
        "isDraft",
        "consumerNumber",
        "customerDetail",
        "authorizedPerson.isAuthorizedPerson",
        "connectionLoad",
        "consumerData",
        "utilityAddress",
        "authorizedPerson.isUnRegisteredPerson",
        "declaration.isConsumerDuesOutstanding",
        "declaration.isPremisesDuesOutstanding",
        "declaration.isFirmDuesOutstanding",
        "authorizedPerson.isEditablePerson",
        "parentId",
        "proofOfOwnershipDescription",
        "documents",
        "consumerName",
        "extraValues",
        "isIGSTExempted",
        imageKeyToRemove,
      ]
    );

    const filteredValues = filterEmptyStrings(applicationDetails);
    // remove entries that contain only id field

    const payload = Object.entries(filteredValues).reduce(
      (res, [key, value]) => {
        if (
          typeof value !== "object" ||
          value instanceof File ||
          Object.keys(value).length > 1
        ) {
          return { ...res, [key]: value };
        }
        return res;
      },
      {}
    );
    const formData = new FormData();
    generateFormData(formData, {
      ...payload,
      declaration: filteredValues.declaration,
    });

    try {
      await submitApplication(formData);

      // If submitted then navigate to my-request
      if (!applicationId) {
        navigate(myRequestRoute);
      }
      handleClose();
    } catch (error) {}
  };

  const handleDraft = () => {
    createNameTransferConnection(formik.values, true);
  };

  return (
    <>
      <div className={!applicationId ? "h-100" : ""}>
        <Backdrop
          open={
            isApplicationDataLoading ||
            isConsumerDetailsLoading ||
            isApplicationDataFetching ||
            isConsumerDataFetching ||
            (!!applicationId && !consumerData)
          }
        />
        {!applicationId && (
          <BreadCrumb
            title={t("Name Change/Transfer Connection")}
            pageTitle={t("Power Applications")}
          />
        )}
        <div className="stepper-container">
          <Stepper
            steps={steps}
            handleSubmit={formik.handleSubmit}
            handleDraft={handleDraft}
            validateDocument={validateDocument}
            activeStepIndex={currentStep}
            setActiveStepIndex={setCurrentStep}
            isLoading={isLoading}
            formik={formik}
            isDraftDisabled={true}
          />
        </div>
      </div>
    </>
  );
};

NameTransfer.propTypes = {
  applicationId: PropTypes.number,
  handleClose: PropTypes.func,
  t: PropTypes.func,
};

export default withTranslation()(NameTransfer);
